import React, { useEffect, useState } from 'react';
import {
    Typography,
    Paper,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Stack,
} from '@mui/material';
import API_URL from '../../settings';

function VulnerableList() {
    const [vulnerable, setVulnerable] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${API_URL}/patients`);
                const data = await res.json();

                const mapped = data
                    .map(p => {
                        const reasons = [];

                        // 💰 เศรษฐกิจ
                        if (
                            p.economy?.status === 'รายได้น้อย' ||
                            p.economy?.status === 'ไม่มี'
                        ) {
                            reasons.push('เศรษฐกิจ');
                        }

                        // ❤️ สุขภาพ
                        const adlGroup = p.adl_summary?.latest_group;

                        if (adlGroup === 'Bedridden' || adlGroup === 'Homebound') {
                            reasons.push('สุขภาพ');
                        }
                        if (reasons.length === 0) return null;

                        return {
                            ...p,
                            vulnerable_reasons: reasons,
                        };
                    })
                    .filter(Boolean);

                setVulnerable(mapped);
            } catch (error) {
                console.error('Failed to fetch vulnerable patient data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);


    function getHealthRisk(p) {
        // 1. ใช้ ADL เป็นหลัก
        const adl = p.adl_summary?.latest_group;

        if (adl === 'Bedridden') {
            return { level: 'สูง', color: 'error' };
        }

        if (adl === 'Homebound') {
            return { level: 'กลาง', color: 'warning' };
        }

        // 2. fallback ใช้ risk_group เดิม
        if (p.risk_group === 'ฉุกเฉิน') {
            return { level: 'สูง', color: 'error' };
        }

        if (p.risk_group === 'ดูแลพิเศษ') {
            return { level: 'กลาง', color: 'warning' };
        }

        // 3. default
        return { level: 'ต่ำ', color: 'success' };
    }


    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold' }}>
                กลุ่มเปราะบาง
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                พิจารณาจากด้านเศรษฐกิจและสุขภาพ
            </Typography>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ชื่อ-สกุล</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>อายุ</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>อาชีพ</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>แหล่งรายได้</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>เหตุผลที่จัดเป็นกลุ่มเปราะบาง</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>ระดับความเสี่ยงสุขภาพ</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>หมายเหตุ</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {vulnerable.length > 0 ? (
                            vulnerable.map(p => (
                                <TableRow key={p.id} hover>
                                    <TableCell>
                                        {p.first_name} {p.last_name}
                                    </TableCell>
                                    <TableCell>{p.age || '-'}</TableCell>
                                    <TableCell>{p.economy?.occupation || '-'}</TableCell>
                                    <TableCell>{p.economy?.source || '-'}</TableCell>

                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            {p.vulnerable_reasons.map(r => (
                                                <Chip
                                                    key={r}
                                                    label={r}
                                                    color={r === 'สุขภาพ' ? 'error' : 'warning'}
                                                    size="small"
                                                />
                                            ))}
                                        </Stack>
                                    </TableCell>

                                    <TableCell>
                                        {(() => {
                                            const risk = getHealthRisk(p);
                                            return (
                                                <Chip
                                                    label={risk.level}
                                                    color={risk.color}
                                                    size="small"
                                                />
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell>{p.economy?.notes || '-'}</TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    ไม่พบข้อมูลกลุ่มเปราะบาง
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default VulnerableList;
